
import React, { useState, useEffect, useMemo } from 'react';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Services from './components/Services';
import DetailedServices from './components/DetailedServices';
import IntelligenceHub from './components/IntelligenceHub';
import About from './components/About';
import Values from './components/Values';
import Industries from './components/Industries';
import Contact from './components/Contact';
import Footer from './components/Footer';
import SentrixAssistant from './components/SentrixAssistant';

const CosmicBackground: React.FC = () => {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const stars = useMemo(() => {
    return Array.from({ length: 200 }).map((_, i) => ({
      id: i,
      size: Math.random() * 1.5 + 0.5,
      top: `${Math.random() * 100}%`,
      left: `${Math.random() * 100}%`,
      duration: `${Math.random() * 3 + 3}s`,
      delay: `${Math.random() * 5}s`,
    }));
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePos({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20,
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <div className="stars-container">
      <div 
        className="absolute inset-0 transition-transform duration-700 ease-out pointer-events-none"
        style={{ transform: `translate(${mousePos.x * 0.3}px, ${mousePos.y * 0.3}px)` }}
      >
        {stars.map((star) => (
          <div
            key={star.id}
            className="star"
            style={{
              width: `${star.size}px`,
              height: `${star.size}px`,
              top: star.top,
              left: star.left,
              '--duration': star.duration,
              animationDelay: star.delay,
            } as React.CSSProperties}
          />
        ))}
      </div>
      
      <div 
        className="nebula w-[1300px] h-[1300px] top-[-35%] left-[-15%] bg-[#00d1ff]/10 transition-transform duration-1000 ease-out"
        style={{ transform: `translate(${mousePos.x * 1.5}px, ${mousePos.y * 1.5}px)` }}
      />
      <div 
        className="nebula w-[1000px] h-[1000px] bottom-[-20%] right-[-10%] bg-[#7000ff]/08 transition-transform duration-1000 ease-out"
        style={{ transform: `translate(${-mousePos.x * 1.2}px, ${-mousePos.y * 1.2}px)` }}
      />
      
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff01_1px,transparent_1px),linear-gradient(to_bottom,#ffffff01_1px,transparent_1px)] bg-[size:100px_100px] opacity-30" />
    </div>
  );
};

export type ViewType = 'home' | 'services' | 'intelligence';

const App: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [currentView, setCurrentView] = useState<ViewType>('home');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigateTo = (view: ViewType, sectionId?: string) => {
    setCurrentView(view);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    if (view === 'home' && sectionId) {
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };

  return (
    <div className="min-h-screen bg-[#02040a] text-slate-100 selection:bg-brand-500/30 font-sans">
      <CosmicBackground />

      <div className="relative z-10">
        <Navbar 
          isScrolled={isScrolled} 
          currentView={currentView}
          onNavigate={navigateTo}
        />
        
        <main>
          {currentView === 'home' && (
            <>
              <Hero onExplore={() => setCurrentView('services')} />
              <About />
              <Services onViewDetailed={() => setCurrentView('services')} />
              <Values />
              <Industries />
              <Contact />
            </>
          )}
          {currentView === 'services' && <DetailedServices />}
          {currentView === 'intelligence' && <IntelligenceHub />}
        </main>

        <Footer onNavigate={navigateTo} />
        <SentrixAssistant />
      </div>
    </div>
  );
};

export default App;
